import { FunctionComponent, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Page } from 'core/ui';

import { useQueryStudySharePublic } from 'features/api/hooks/useQueryStudySharePublic';
import { useAuthentication, useCurrentUser } from 'features/auth';
import { AuthenticationScheme } from 'features/auth/constants';

export const ShareUploadAuthorizationChallenge: FunctionComponent = () => {
  const navigate = useNavigate();
  const { loginShare, activeScheme } = useAuthentication();
  const { currentUser } = useCurrentUser();
  const { linkId } = useParams<{ linkId: string }>();

  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

  if (!linkId) {
    throw new Error('ShareUploadAuthorizationChallenge requires linkId parameter');
  }

  const { data: share, isPending: isShareQueryPending, isError: isShareQueryError } = useQueryStudySharePublic(linkId);

  // Auto-login for upload routes or shares with byPassSplashPage - attempt to bypass landing page
  useEffect(() => {
    if (share && !isAutoLoggingIn && !autoLoginAttempted) {
      const isUploadRoute = location.pathname.includes('/upload');
      const shouldBypassSplash = isUploadRoute || share.byPassSplashPage;

      if (shouldBypassSplash) {
        setIsAutoLoggingIn(true);
        setAutoLoginAttempted(true);

        // Build user claims if this is an OIDC user
        const userClaims =
          activeScheme === AuthenticationScheme.OIDC && currentUser && currentUser !== 'anonymous'
            ? {
                email: currentUser.email,
                name: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
              }
            : undefined;

        // Attempt auto-login with bypassed splash page (true for bypassSplashPage)
        loginShare(linkId, null, true, userClaims)
          .then((result) => {
            if (result.success) {
              // Auto-login succeeded - navigate directly to intended route
              const currentPath = `${location.pathname}${location.search}${location.hash}`;
              navigate(currentPath, { replace: true });
            } else {
              // Auto-login failed - redirect to landing page for manual login
              console.warn('Share auto-login failed:', result.errorCode, 'for route:', location.pathname);
              const returnRoute = `${location.pathname}${location.search}${location.hash}`;
              navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`, { replace: true });
            }
          })
          .catch((error: unknown) => {
            // Auto-login error - redirect to landing page
            console.error('Share auto-login error:', error);
            const returnRoute = `${location.pathname}${location.search}${location.hash}`;
            navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`, { replace: true });
          })
          .finally(() => {
            setIsAutoLoggingIn(false);
          });
      } else {
        // Not an upload route and no byPassSplashPage - redirect to landing immediately
        const returnRoute = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`, { replace: true });
      }
    }
  }, [share, linkId, loginShare, activeScheme, currentUser, navigate, isAutoLoggingIn, autoLoginAttempted]);

  // Handle query errors
  if (isShareQueryError) {
    throw new Error('Error loading share for upload route.');
  }

  // Show loading spinner while fetching share data or attempting auto-login
  if (isShareQueryPending || isAutoLoggingIn || !autoLoginAttempted) {
    return (
      <Page>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span>Preparing upload interface...</span>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </Page>
    );
  }

  // If we reach here, something went wrong - redirect to landing as fallback
  if (share) {
    const returnRoute = `${location.pathname}${location.search}${location.hash}`;
    navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`, { replace: true });
  }

  return null;
};

ShareUploadAuthorizationChallenge.displayName = 'ShareUploadAuthorizationChallenge';
